/**
 * BrandMonogram — the mark we draw for a company that is not us.
 *
 * WHAT THIS IS FOR. The compare pages DO show a competitor's real favicon — nominative use on a
 * page called "askFinz vs Apify" — but only ever through app/api/favicon, our own server-side
 * proxy. This monogram is what renders when there is no favicon to show: a page comparing a
 * CATEGORY rather than a company, a domain whose mark cannot be fetched, and every page before
 * hydration. It is also the whole answer on surfaces with no domain to key on.
 *
 * WHAT IS STILL FORBIDDEN, and why the proxy exists rather than an <img> at their origin:
 *
 *   1. Trademark. The mark is theirs, so no copy of it is committed to this repo — it is fetched
 *      at request time and cached, never vendored.
 *   2. Reliability. An asset on someone else's origin can 404, rate-limit, or block us, and it
 *      does so silently — the row just loses its icon and nobody notices for months.
 *   3. Privacy. A hotlinked favicon tells that service which askFinz pages a visitor is reading
 *      and which companies we compare ourselves to. `app/api/favicon/route.ts` exists in this
 *      codebase specifically to avoid that, and the compare pages were bypassing it.
 *
 * What is drawn instead is generated from the company's own NAME with askFinz design tokens —
 * --card surface, hairline border, --accent red, Geist Mono. It is deterministic (FNV-1a over the
 * name picks the accent edge and surface tint, so the same name always yields the same tile), and
 * it is pure CSS with a self-hosted font, so it renders identically offline, inside a Docker build
 * with no network, and on a cold CDN. Nothing here can fail to load.
 */

/* Suffix noise that carries no identity — "Notion AI" is Notion, not NA. Dropped before initials
   are taken, unless dropping them would leave nothing. Kept deliberately short: "Search", "Web"
   and "Data" are NOT here, because "Bright Data" is BD and "Common Crawl" is CC. */
const GENERIC = new Set(["api", "apis", "ai", "the", "and", "inc", "com", "io"]);

/** Initials for a name with no author-supplied monogram. Uppercase, at most `max` characters. */
function deriveMono(name: string, max: number): string {
  const words = name.replace(/[^A-Za-z0-9]+/g, " ").trim().split(" ").filter(Boolean);
  const core = words.filter((w) => !GENERIC.has(w.toLowerCase()));
  const use = core.length ? core : words;
  if (max <= 1) return (use[0]?.[0] || "?").toUpperCase();
  if (use.length >= 2) return (use[0][0] + use[1][0]).toUpperCase();
  const w = use[0] || "?";
  return (w[0] + (w[1] || "")).toUpperCase();
}

/**
 * The author's monogram wins, because only an author can tell SerpApi from Serper. But a tile
 * fits three characters, not "Bright Data" — anything longer is reduced to initials rather than
 * allowed to overflow, which is what it used to do: an 11-character word at 22px inside a 56px
 * hub tile, clipped at both edges by the card's own overflow-hidden.
 */
export function markLabel(name: string, mono?: string, max = 3): string {
  const clean = (mono || "").replace(/[^A-Za-z0-9]/g, "");
  if (clean.length >= 1 && clean.length <= max) return clean;
  return deriveMono(clean || name, max);
}

/** FNV-1a. Stable across builds and machines — a tile must not change between deploys. */
function hashName(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/* Four accent placements x three surface tints = twelve tiles, picked by the name's hash. Enough
   that a row of comparison cards does not look stamped from one die; small enough that every tile
   is recognisably the same family. */
const ACCENT_EDGE = [
  "left-[18%] right-[18%] top-0 h-[2px]",
  "left-[18%] right-[18%] bottom-0 h-[2px]",
  "top-[18%] bottom-[18%] left-0 w-[2px]",
  "top-[18%] bottom-[18%] right-0 w-[2px]",
];
const TINT = ["0.015", "0.035", "0.055"];

/* Character count drives the type size, and the size is in cqw against the tile itself, so one
   component serves the 20px table chip, the 56px hub card and the 112px page hero without a
   per-call-site font override. The tile declares `container-type: inline-size` for exactly that
   reason: the code this replaced used cqw with NO container query container anywhere above it, so
   the unit silently resolved against the VIEWPORT and the glyph was sized by the browser window
   rather than by the tile it sat in. */
/* Percent of the tile's own width, per glyph count. Geist Mono advances ~0.6em, so three
   characters at 27cqw occupy ~49% of the tile and one at 52cqw occupies ~31% — comfortable at
   both the 20px table chip and the 112px hero without ever touching the edges. */
const SIZE_BY_LEN: Record<number, number> = { 1: 52, 2: 38, 3: 27 };

export function BrandMonogram({
  name,
  mono,
  maxChars = 3,
  className = "h-20 w-20 rounded-2xl",
}: {
  /** The company's name. Also the hash input, so the tile is stable per company. */
  name: string;
  /** Optional author override, e.g. "GPT" for ChatGPT. Trimmed to `maxChars`. */
  mono?: string;
  /** 1 for small chips where two glyphs would be unreadable; 3 for hero tiles. */
  maxChars?: number;
  /** Size + radius. Sizing lives at the call site; everything else is fixed here. */
  className?: string;
}) {
  const label = markLabel(name, mono, maxChars);
  const h = hashName(name);
  const edge = ACCENT_EDGE[h % ACCENT_EDGE.length];
  const tint = TINT[(h >>> 3) % TINT.length];
  const size = SIZE_BY_LEN[label.length] ?? 27;
  /* A <span> with display:grid, not a <div> — the price table on /compare/search-apis places this
     inside a <span>, and a <div> there is invalid nesting. */
  return (
    <span
      aria-hidden
      className={`relative grid shrink-0 place-items-center overflow-hidden border hairline-strong bg-card ${className}`}
      style={{ containerType: "inline-size" }}
    >
      <span className="absolute inset-0" style={{ background: `hsl(var(--foreground) / ${tint})` }} />
      <span className={`absolute bg-accent ${edge}`} />
      <span
        className="relative font-mono font-medium leading-none tracking-tight text-foreground"
        style={{ fontSize: `${size}cqw` }}
      >
        {label}
      </span>
    </span>
  );
}
